/**
 * AppErrorBoundary -- App-Level Error Boundary
 * Step 9F Green Phase -- Xyden Leadership Assessment Platform
 *
 * Catches unhandled errors in the React component tree.
 * Shows a fallback UI with alert role and a retry button.
 * On retry, resets error state and re-renders children with a new key
 * to force complete re-mount (allowing transient errors to recover).
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportClientError } from '../lib/telemetry';

interface AppErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // First-party crash reporting (NFR-F-OBS-001). An app-shell crash used to
    // leave no trace at all (both args were discarded); now it's shipped to the
    // in-house telemetry sink so UAT/prod crashes are diagnosable.
    // reportClientError never throws, so it's safe to call from here.
    reportClientError(error, errorInfo, 'render');
  }

  handleRetry = (): void => {
    this.setState(prev => ({
      hasError: false,
      error: null,
      retryCount: prev.retryCount + 1,
    }));
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div role="alert" className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-brand-navy mb-4">
              <span className="text-lg font-bold text-white tracking-tight">XYDEN</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-sm text-gray-600 mb-6">
              An unexpected error occurred. Please try again.
            </p>
            <button
              type="button"
              onClick={this.handleRetry}
              className="px-4 py-2 bg-brand-crimson text-white rounded-lg text-sm font-medium hover:bg-brand-crimson/90"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    // Use retryCount as key to force full re-mount on retry
    return <React.Fragment key={this.state.retryCount}>{this.props.children}</React.Fragment>;
  }
}

export default AppErrorBoundary;
